import { z } from "zod";
/**
 * Standard live-sound cable lengths (metres) used for stocking and packing trunks.
 */
export const STANDARD_CABLE_LENGTHS = [3, 5, 10, 15, 20];
export const CableTypeSchema = z.enum([
    "xlr_mic",
    "xlr_line",
    "speakon",
    "power_shuko",
    "power_cEE",
    "cat5e_ethercon",
    "cat6_ethercon",
    "optical_fiber",
    "coaxial_bnc",
]);
/**
 * Signal Port Direction (§SCRUM-287):
 * - `source`: Audio signal producer (mic, instrument, line out).
 * - `sink`: Audio signal receiver/consumer (monitor wedge, IEM transmitter, PA feed, speaker).
 */
export const SignalDirectionSchema = z.enum(["source", "sink"]);
/**
 * Signal Voltage / Nominal Level (§SCRUM-287).
 */
export const SignalLevelSchema = z.enum(["mic", "line", "instrument", "speaker"]);
/**
 * Physical Medium of Signal Transmission (§SCRUM-287).
 * Used to distinguish cabled physical lines from RF radio and acoustic propagation.
 */
export const SignalMediumSchema = z.enum([
    "copper_analog", // XLR / TRS / Speakon analog copper
    "copper_digital", // AES/EBU, AES50, StageConnect
    "network", // Dante, AVB, gigaACE, Ravenna
    "optical", // MADI optical, Optocore
    "rf", // Wireless radio frequency (handheld mic capsule, in-ear bodypack receiver)
    "acoustic", // Acoustic sound waves in air (speaker cone, guitar cabinet)
]);
/**
 * Physical Termination Location of Port Connector (§SCRUM-287).
 * Decides whether a floor cable route across the stage is required or refused.
 */
export const TerminatesAtSchema = z.enum([
    "at_object", // Connector physically on stage object/stand/floor
    "monitor_rack", // Connector on rack unit beside StageBox (IEM transmitter, radio-mic receiver)
    "foh", // Connector in front-of-house mix position
    "not_cabled", // Physical cable connection not applicable (RF/acoustic)
]);
/**
 * Typed Physical Signal Port contract (§SCRUM-287).
 */
export const SignalPortSchema = z
    .object({
    direction: SignalDirectionSchema,
    level: SignalLevelSchema,
    medium: SignalMediumSchema,
    terminatesAt: TerminatesAtSchema,
    connector: CableTypeSchema.optional(),
    count: z.number().int().min(1).default(1),
})
    .strict();
export const STANDARD_STAGE_BOX_MODELS = [
    {
        id: "digico-sd-rack-56-32",
        catalogKey: "digico-sd-rack",
        name: "DiGiCo SD-Rack (56 In / 32 Out)",
        manufacturer: "DiGiCo",
        inputsCount: 56,
        outputsCount: 32,
        description: "Standard large-format festival/touring rack with 32-bit 'Stadius' mic preamps",
    },
    {
        id: "digico-d2-rack-48-24",
        catalogKey: "digico-d2-rack",
        name: "DiGiCo D2-Rack (48 In / 24 Out)",
        manufacturer: "DiGiCo",
        inputsCount: 48,
        outputsCount: 24,
        description: "Compact 9U touring rack (48 mic in, 24 analogue out)",
    },
    {
        id: "yamaha-rio-3224-d2",
        catalogKey: "yamaha-rio3224-d2",
        name: "Yamaha Rio3224-D2 (32 In / 24 Out)",
        manufacturer: "Yamaha",
        inputsCount: 32,
        outputsCount: 24,
        description: "RIVAGE PM / CL / QL Dante I/O stage rack with 4 AES/EBU outs",
    },
    {
        id: "yamaha-rio-1608-d2",
        catalogKey: "yamaha-rio1608-d2",
        name: "Yamaha Rio1608-D2 (16 In / 8 Out)",
        manufacturer: "Yamaha",
        inputsCount: 16,
        outputsCount: 8,
        description: "Compact 3U Dante stage rack for secondary positions",
    },
    {
        id: "midas-dl251-48-16",
        catalogKey: "midas-dl251",
        name: "Midas DL251 (48 In / 16 Out)",
        manufacturer: "Midas",
        inputsCount: 48,
        outputsCount: 16,
        description: "Fixed-format I/O unit for Heritage-D / PRO series consoles",
    },
    {
        id: "ah-gx4816-48-16",
        catalogKey: "allen-heath-gx4816",
        name: "Allen & Heath GX4816 (48 In / 16 Out)",
        manufacturer: "Allen & Heath",
        inputsCount: 48,
        outputsCount: 16,
        description: "96kHz dLive / SQ expander rack with ME personal mixing ports",
    },
    {
        id: "midas-dl32-32-16",
        catalogKey: "midas-dl32",
        name: "Midas DL32 / X32 S32 (32 In / 16 Out)",
        manufacturer: "Midas / Behringer",
        inputsCount: 32,
        outputsCount: 16,
        description: "32 Midas Pro preamps and 16 servo-balanced outputs (AES50)",
    },
    {
        id: "optocore-dd64-64-32",
        catalogKey: null,
        name: "Optocore / Stagetec (64 In / 32 Out)",
        manufacturer: "Optocore",
        inputsCount: 64,
        outputsCount: 32,
        description: "64-channel modular optical stage network interface",
    },
    {
        id: "custom-stage-box-64-32",
        catalogKey: null,
        name: "Custom Stage Hub (64 In / 32 Out)",
        manufacturer: "Custom",
        inputsCount: 64,
        outputsCount: 32,
        description: "Custom stage box configuration",
    },
    // Current-generation boxes, added for SCRUM-361 (the mixing system a rider needs when it names
    // none). I/O checked by the sound-engineer agent on 2026-09-24; sources in
    // knowledge/domain/mixing-consoles.md#console-capacity-current. Card-based chassis (Yamaha RPio622/
    // RPio222, Allen & Heath DX32) and the DL431 splitter have no fixed I/O and are deliberately absent.
    {
        id: "yamaha-rio3224-d3",
        catalogKey: "yamaha-rio3224-d3",
        name: "Yamaha Rio3224-D3 (32 In / 24 Out)",
        manufacturer: "Yamaha",
        inputsCount: 32,
        outputsCount: 24,
        description: "Current Dante I/O rack for DM7 / RIVAGE PM; 32 analog in, 16 analog out + 8 AES/EBU out",
    },
    {
        id: "yamaha-rio1608-d3",
        catalogKey: "yamaha-rio1608-d3",
        name: "Yamaha Rio1608-D3 (16 In / 8 Out)",
        manufacturer: "Yamaha",
        inputsCount: 16,
        outputsCount: 8,
        description: "Current compact Dante I/O rack for DM7 / RIVAGE PM",
    },
    {
        id: "midas-dl231-24-24",
        catalogKey: "midas-dl231",
        name: "Midas DL231 (24 In / 24 Out)",
        manufacturer: "Midas",
        inputsCount: 24,
        outputsCount: 24,
        description: "Active mic splitter stage box, dual redundant AES50, native to HD96-24",
    },
    {
        id: "midas-dl252-16-48",
        catalogKey: "midas-dl252",
        name: "Midas DL252 (16 In / 48 Out)",
        manufacturer: "Midas",
        inputsCount: 16,
        outputsCount: 48,
        description: "Fixed-format output-heavy I/O unit for PRO series / HD96-24",
    },
    {
        id: "ah-dx168-16-8",
        catalogKey: "allen-heath-dx168",
        name: "Allen & Heath DX168 (16 In / 8 Out)",
        manufacturer: "Allen & Heath",
        inputsCount: 16,
        outputsCount: 8,
        description: "96kHz stage box for dLive / Avantis / SQ, daisy-chainable",
    },
    {
        id: "behringer-s16-16-8",
        catalogKey: "behringer-s16",
        name: "Behringer S16 (16 In / 8 Out)",
        manufacturer: "Behringer",
        inputsCount: 16,
        outputsCount: 8,
        description: "AES50 stage box for WING / X32 / M32, cascadable",
    },
    {
        id: "avid-stage64-64-32",
        catalogKey: "avid-venue-stage-64",
        name: "Avid Stage 64 (64 In / 32 Out)",
        manufacturer: "Avid",
        inputsCount: 64,
        outputsCount: 32,
        description: "Fixed I/O rack for VENUE S6L",
    },
    {
        id: "avid-stage16-16-8",
        catalogKey: "avid-venue-stage-16",
        name: "Avid Stage 16 (16 In / 8 Out)",
        manufacturer: "Avid",
        inputsCount: 16,
        outputsCount: 8,
        description: "Compact VENUE S6L I/O rack",
    },
];
export const STANDARD_SUB_SNAKE_PRESETS = [
    { id: "sub-12-8", name: "Sub-Snake 12/8 (12 Sends / 8 Returns)", sendsCount: 12, returnsCount: 8, defaultTailMeters: 20 },
    { id: "sub-12-4", name: "Sub-Snake 12/4 (12 Sends / 4 Returns)", sendsCount: 12, returnsCount: 4, defaultTailMeters: 15 },
    { id: "sub-8-4", name: "Sub-Snake 8/4 (8 Sends / 4 Returns)", sendsCount: 8, returnsCount: 4, defaultTailMeters: 15 },
    { id: "sub-8-0", name: "Sub-Snake 8/0 (8 Sends Drum/Mic Box)", sendsCount: 8, returnsCount: 0, defaultTailMeters: 15 },
    { id: "sub-4-2", name: "Drop-Box 4/2 (4 Sends / 2 Returns Downstage)", sendsCount: 4, returnsCount: 2, defaultTailMeters: 10 },
];
/**
 * AD/DA Stage Box / Hub on stage (Slice 1: Single primary AD/DA hub, e.g. Stage Left rack).
 */
export const StageBoxSchema = z
    .object({
    id: z.string().max(64),
    code: z.string().max(50).default("HUB1"),
    name: z.string().max(255),
    inputsCount: z.number().int().min(1).max(128),
    outputsCount: z.number().int().min(0).max(64),
    x: z.number(),
    y: z.number(),
})
    .strict();
/**
 * SubSnake Connector Type (§10.1a).
 * - `integrated_tail`: Subsnake box with permanently attached fan-out multicore tail.
 * - `harting`: Multipin connector on box requiring separate multicore trunk cable.
 */
export const SubSnakeConnectorSchema = z.enum(["integrated_tail", "harting"]);
/**
 * SubSnake Physical Form Factor (§10.1a).
 * - `floor_box`: Stage floor drop box.
 * - `rack`: 19" rack-mounted analogue breakout box.
 */
export const SubSnakeFormSchema = z.enum(["floor_box", "rack"]);
/**
 * Раскладка выводов не по порядку: гнездо сабснейка -> гнездо стейджбокса. Потолки — те же, что у самого
 * железа: в сабснейке не больше 48 гнёзд, в стейджбоксе не больше 128 (запись приходит снаружи, и без
 * потолка в оффер ложится сколько угодно ключей).
 */
const PinoutMappingSchema = z
    .record(z.coerce.number(), z.number().int().min(1).max(128))
    .refine((m) => Object.keys(m).length <= 64, { message: "at most 64 sockets in a custom pinout" });
/**
 * Analogue passive sub-snake / breakout (§10.1a & §10.2).
 */
export const SubSnakeSchema = z
    .object({
    id: z.string().max(64),
    code: z.string().max(50).optional(),
    /**
     * МЁРТВОЕ ПОЛЕ: ПОДПИСЬ СЧИТАЕТСЯ, А НЕ ХРАНИТСЯ. Сабснейк на экране и в печати называется
     * `snakeName(x, y, sends, returns)` — зона сцены плюс ёмкость (владелец 26.10.2026: «мы
     * называем сабснейки по позициям»). Это поле больше не читает НИКТО, и на том стоит сторож
     * `patch-snake-name-is-dead.test.ts`: пока его не читают, старое «drum subsnake» неоткуда
     * взяться на экране, хотя оно и лежит в сохранённых патчах.
     *
     * Почему не удалено: схема `.strict()`, и уход поля уронил бы разбор каждого УЖЕ сохранённого
     * патча. Удалять его — вместе с решением, где патч живёт постоянно, а оно за владельцем («мы
     * оставляем все необходимые для этого подключения и функции но не углубляемся сейчас»).
     */
    name: z.string().max(255),
    connector: SubSnakeConnectorSchema.default("integrated_tail"),
    form: SubSnakeFormSchema.default("floor_box"),
    sendsCount: z.number().int().min(1).max(48),
    returnsCount: z.number().int().min(0).max(24),
    tailLengthMeters: z.number().min(1).max(100),
    x: z.number(),
    y: z.number(),
    /** Default starting input socket on primary StageBox (e.g. 1 for SS1 -> In 1..12, 13 for SS2 -> In 13..20) */
    hubInputStart: z.number().int().min(1).max(128).optional(),
    /** Default starting return socket on primary StageBox */
    hubReturnStart: z.number().int().min(1).max(128).optional(),
    /** Custom/non-straight pinout mapping: subsnake socket (1..N) -> stagebox input socket (1..M) */
    hubInputMapping: PinoutMappingSchema.optional(),
    /** Custom/non-straight pinout mapping: subsnake return socket (1..N) -> stagebox output socket (1..M) */
    hubReturnMapping: PinoutMappingSchema.optional(),
})
    .strict();
/**
 * Resolves the destination StageBox input socket for a given SubSnake send socket.
 * Straight (1-to-1) by default: hubInputStart + socket - 1, or custom non-straight pinout.
 */
export function getSubSnakeHubInputSocket(snake, socket, defaultStart = 1) {
    if (snake.hubInputMapping && snake.hubInputMapping[socket]) {
        return snake.hubInputMapping[socket];
    }
    const start = snake.hubInputStart ?? defaultStart;
    return start + socket - 1;
}
/**
 * Resolves the destination StageBox output socket for a given SubSnake return socket.
 * Straight (1-to-1) by default: hubReturnStart + socket - 1, or custom non-straight pinout.
 */
export function getSubSnakeHubReturnSocket(snake, socket, defaultStart = 1) {
    if (snake.hubReturnMapping && snake.hubReturnMapping[socket]) {
        return snake.hubReturnMapping[socket];
    }
    const start = snake.hubReturnStart ?? defaultStart;
    return start + socket - 1;
}
export const RouteDirectionSchema = z.enum(["input", "output"]);
/**
 * Helper to format standard live patch notation: (port/subsnake to port/stagebox)
 * e.g. "01/SS1 to 01/HUB1" or "DIR to 28/HUB1"
 */
export function formatPatchNotation(subSnakeSocket, subSnakeCode, stageBoxSocket, stageBoxCode = "HUB1", direction = "input") {
    const hubPortStr = String(stageBoxSocket).padStart(2, "0");
    if (direction === "output") {
        if (subSnakeSocket !== undefined && subSnakeCode) {
            const snakePortStr = String(subSnakeSocket).padStart(2, "0");
            return `from Out ${hubPortStr}/${stageBoxCode} to R${snakePortStr}/${subSnakeCode}`;
        }
        return `from Out ${hubPortStr}/${stageBoxCode}`;
    }
    if (subSnakeSocket !== undefined && subSnakeCode) {
        const snakePortStr = String(subSnakeSocket).padStart(2, "0");
        return `${snakePortStr}/${subSnakeCode} to In ${hubPortStr}/${stageBoxCode}`;
    }
    return `DIR to In ${hubPortStr}/${stageBoxCode}`;
}
/**
 * Commutation route for a single audio input channel or output mix line.
 */
export const PatchRouteSchema = z
    .object({
    direction: RouteDirectionSchema.default("input"),
    channelNumber: z.number().int().min(1),
    sourceName: z.string(),
    ownerUuid: z.string(),
    port: SignalPortSchema.optional(),
    subSnakeId: z.string().optional(),
    subSnakeCode: z.string().optional(),
    subSnakeSocket: z.number().int().min(1).optional(),
    stageBoxId: z.string(),
    stageBoxCode: z.string().default("HUB1"),
    stageBoxSocket: z.number().int().min(1),
    patchNotation: z.string().optional(),
    cableLengthMeters: z.number().min(1),
    cableType: CableTypeSchema.default("xlr_mic"),
    /** Waypoints for 2D/3D cable visualization along stage perimeter (source -> subsnake -> stagebox) */
    waypoints: z.array(z.object({ x: z.number(), y: z.number() })).optional(),
})
    .strict();
/**
 * Recommendation for an optimal sub-snake set and placement.
 */
export const SubSnakeRecommendationSchema = z
    .object({
    suggestedSubSnakes: z.array(SubSnakeSchema),
    totalCablingMetersWithoutSubSnakes: z.number(),
    totalCablingMetersWithSubSnakes: z.number(),
    cableSavingsMeters: z.number(),
    percentageSaved: z.number(),
    reasons: z.array(z.string()),
})
    .strict();
/**
 * Aggregated cable count by standard length and type.
 */
export const CableSummaryItemSchema = z
    .object({
    lengthMeters: z.number(),
    count: z.number().int().min(0),
    type: CableTypeSchema,
})
    .strict();
/**
 * Capacity report for inputs and outputs (§10.2).
 * Shows used, total, and remaining capacity as structured report rather than just a boolean.
 */
export const CapacityReportSchema = z
    .object({
    inputsTotal: z.number().int().min(0),
    inputsUsed: z.number().int().min(0),
    inputsRemaining: z.number().int(),
    outputsTotal: z.number().int().min(0),
    outputsUsed: z.number().int().min(0),
    outputsRemaining: z.number().int(),
    isInputsExceeded: z.boolean(),
    isOutputsExceeded: z.boolean(),
})
    .strict();
/**
 * Complete Stage Patch Document (Slice 1: Single AD/DA Hub + SubSnakes Star Topology).
 */
export const StagePatchDocumentSchema = z
    .object({
    schemaVersion: z.literal(1),
    riderId: z.string().optional(),
    stageBox: StageBoxSchema,
    subSnakes: z.array(SubSnakeSchema),
    routes: z.array(PatchRouteSchema),
    cableSummary: z.array(CableSummaryItemSchema),
    capacityReport: CapacityReportSchema,
    recommendation: SubSnakeRecommendationSchema.optional(),
})
    .strict();
/**
 * ЧТО ХРАНИТСЯ, А ЧТО СЧИТАЕТСЯ (решение по контракту, 26.10.2026).
 *
 * Владелец: «теперь мы упёрлись в то что у нас не подключен сюда патч-эдитор» — патч существовал
 * только в `localStorage` браузера и не доезжал ни до райдера, ни до бригады, ни до оффера
 * (`docs/V2_PATCH_EDITOR_BRIEF.md` §0.1).
 *
 * Хранится РОВНО ТО, ЧТО АВТОРСТВУЕТ ЧЕЛОВЕК: выбранный стейджбокс и сабснейки. Маршруты,
 * кабельная сводка и отчёт о ёмкости — ПРОИЗВОДНОЕ: их считает `solveStagePatch` из райдера и этой
 * конфигурации. Именно это и лежало в `localStorage` (`patch-editor-dialog.ts#persistPatchConfig`),
 * то есть форма не меняется, меняется только место.
 *
 * Почему не сохранить готовый `StagePatchDocument`: он протухает в ту же секунду, когда правят
 * райдер. Сегодня мы уже заплатили за такую ошибку в другом месте — заголовок шоу намертво хранил
 * «venue not set», хотя площадку назначили позже (SCRUM-833). Производное значение не хранят.
 *
 * ГДЕ ЭТО ЖИВЁТ — НЕ ЗДЕСЬ. Форма одинакова, кому бы конфигурация ни принадлежала, и это нарочно:
 * владелец, 26.10.2026, поправляя меня во второй раз — «нет, патч не живёт при райдере и это
 * обсуждалось много раз. Он временно жил при райдере пока не были сделаны модули», и там же
 * назвал оба настоящих источника: **патч возникает на ПЛОЩАДКЕ** (она для нас частный случай
 * проката, который просто живёт на ней — в базе это `venues.resident_supplier_user_id` и
 * `own_sound`) **либо в момент РАСЧЁТА ОФФЕРА**. Решение 11.10.2026 в брифе §0a говорит то же:
 * «оффер и патч по сути это часть воркфлоу прокатчика», хозяин — шоу, эпик SCRUM-647.
 *
 * Поэтому здесь только ФОРМА, без места: ни `riderId`, ни `showId`, ни `venueId`. Тот, кто кладёт
 * её в базу, называет хозяина сам.
 */
export const StagePatchConfigSchema = z
    .object({
    schemaVersion: z.literal(1),
    stageBox: StageBoxSchema,
    /** 64 — потолок записи снаружи (сервер кладёт конфигурацию в строку оффера); живой патч — единицы. */
    subSnakes: z.array(SubSnakeSchema).max(64),
})
    .strict();
/** Структурная проверка по образцу `validateOfferDraft()`: не бросает; строку читает как JSON. */
export function validateStagePatchConfig(input) {
    let candidate = input;
    if (typeof input === "string") {
        try {
            candidate = JSON.parse(input);
        }
        catch (err) {
            return { success: false, errors: [{ path: "$", message: `malformed JSON: ${err.message}` }] };
        }
    }
    const parsed = StagePatchConfigSchema.safeParse(candidate);
    if (!parsed.success) {
        return { success: false, errors: parsed.error.issues.map((i) => ({ path: i.path.join("."), message: i.message })) };
    }
    return { success: true, data: parsed.data };
}
//# sourceMappingURL=patch.js.map